/**
 * FrameJitterReporter (LRG.27 client side) — periodically pushes the
 * FrameJitterTracker snapshot to the server via the
 * `netobs:frameJitter` Colyseus message. Server caches per-session and
 * surfaces it via `GET /api/diag/frame-jitter` for the
 * `dj_h2000_frame_jitter_probe` MCP tool.
 *
 * 5s cadence. Gated
 * on isNetobsEnabled() so prod sessions don't push when nothing's reading.
 *
 * Default-OFF: FrameJitterTracker's `enabled` is false unless the operator
 * flips the checkbox or sets ?framejitter=1. The reporter is still mounted
 * but the tracker returns an empty snapshot (sampleCount=0) — no useful
 * data to push. Server-side buffer just skips empty snapshots.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import { FrameJitterTracker } from '../../diagnostics/FrameJitterTracker';

const REPORT_INTERVAL_MS = 5000;

function sendSnapshot(): void {
  const room = (globalThis as any).nm?.getRoom?.();
  if (!room || typeof room.send !== 'function') return;
  try {
    const snapshot = FrameJitterTracker.getSnapshot();
    // Skip empty snapshots (tracker disabled or no frames captured yet).
    // No useful data to record; reduces wire chatter.
    if (snapshot.sampleCount === 0) return;
    room.send('netobs:frameJitter', snapshot);
  } catch {
    // diagnostic-only — never gameplay
  }
}

const FrameJitterReporterInner: React.FC = () => {
  useEffect(() => {
    const id = setInterval(sendSnapshot, REPORT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  return null;
};

export const FrameJitterReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <FrameJitterReporterInner />;
};
